import * as React from "react";
import { Globe, AlertCircle, AlertTriangle, CheckCircle } from "react-feather";
import { useAppSelector } from "../../store/hooks";
import { selectProcessedDomains } from "../../store/healthSlice";
import { HEALTH_COLORS } from "./utils";
import { Loader } from "../../components/layout/loader";

export const Domains: React.FC = () => {
  const isLoading = useAppSelector((state) => state.health.isLoading);
  const isFetched = useAppSelector((state) => state.health.isFetched);
  const domains = useAppSelector((state) => state.health.domains);
  const processedDomains = useAppSelector(selectProcessedDomains);

  // Categorize domains
  const categorizedDomains = React.useMemo(() => {
    const error = processedDomains.filter((d) => d.error);
    const warning = processedDomains.filter((d) => d.warning && !d.error);
    const ready = domains.length - error.length - warning.length;
    return { error, warning, ready };
  }, [processedDomains, domains]);

  if (isLoading && !isFetched) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pb-16" style={{ maxWidth: 1000 }}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Globe size={20} style={{ color: "var(--color-label)" }} />
          <h1 className="text-xl font-semibold" style={{ color: "var(--color-default)" }}>
            Domain Health
          </h1>
        </div>
        <div className="flex items-center gap-4 text-sm" style={{ color: "var(--color-label)" }}>
          <span className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: HEALTH_COLORS.ready }} />
            {categorizedDomains.ready} ready
          </span>
          {categorizedDomains.warning.length > 0 && (
            <span className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: HEALTH_COLORS.warning }} />
              {categorizedDomains.warning.length} warning
            </span>
          )}
          {categorizedDomains.error.length > 0 && (
            <span className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: HEALTH_COLORS.error }} />
              {categorizedDomains.error.length} error
            </span>
          )}
        </div>
      </div>

      {domains.length === 0 ? (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: "var(--color-drop)", border: "1px solid var(--color-border)" }}
        >
          <div className="text-sm" style={{ color: "var(--color-label)" }}>
            No domains configured
          </div>
        </div>
      ) : processedDomains.length === 0 ? (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: "var(--color-drop)", border: "1px solid var(--color-border)" }}
        >
          <div
            className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3"
            style={{ backgroundColor: "rgba(16, 185, 129, 0.1)" }}
          >
            <CheckCircle size={24} color={HEALTH_COLORS.ready} />
          </div>
          <div className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
            All domains are healthy
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--color-label)" }}>
            {domains.length} domains ready
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Error Domains */}
          {categorizedDomains.error.length > 0 && (
            <div>
              <h2 className="text-sm font-medium mb-3" style={{ color: HEALTH_COLORS.error }}>
                Errors ({categorizedDomains.error.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {categorizedDomains.error.map((domain) => (
                  <DomainCard key={domain.name} domain={domain} severity="error" />
                ))}
              </div>
            </div>
          )}

          {/* Warning Domains */}
          {categorizedDomains.warning.length > 0 && (
            <div>
              <h2 className="text-sm font-medium mb-3" style={{ color: HEALTH_COLORS.warning }}>
                Warnings ({categorizedDomains.warning.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {categorizedDomains.warning.map((domain) => (
                  <DomainCard key={domain.name} domain={domain} severity="warning" />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface DomainCardProps {
  domain: {
    name: string;
    error: boolean;
    warning: boolean;
    status: string;
    text: string;
  };
  severity: "error" | "warning";
}

const DomainCard: React.FC<DomainCardProps> = ({ domain, severity }) => {
  const Icon = severity === "error" ? AlertCircle : AlertTriangle;
  const color = severity === "error" ? HEALTH_COLORS.error : HEALTH_COLORS.warning;

  return (
    <div
      className="rounded-lg p-4"
      style={{
        backgroundColor: "var(--color-drop)",
        border: `1px solid ${color}`,
      }}
    >
      <div className="flex items-start gap-3">
        <Icon size={16} color={color} className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
              {domain.name}
            </span>
            <span
              className="text-xs px-2 py-0.5 rounded"
              style={{
                backgroundColor: severity === "error" ? "rgba(239, 68, 68, 0.1)" : "rgba(245, 158, 11, 0.1)",
                color,
              }}
            >
              {domain.status}
            </span>
          </div>
          <div className="text-xs" style={{ color: "var(--color-label)" }}>
            {domain.text}
          </div>
        </div>
      </div>
    </div>
  );
};
